import React, { useEffect } from "react";
import Modal from "@cloudscape-design/components/modal";
import Input from "@cloudscape-design/components/input";
import Textarea from "@cloudscape-design/components/textarea";
import Select from "@cloudscape-design/components/select";
import FormField from "@cloudscape-design/components/form-field";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Spinner from "@cloudscape-design/components/spinner";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { authBannerSettingsModalStore, createAuthBannerSettingsForm } from "../stores/authBannerSettingsModal";
import { MAX_BANNER_BYTES } from "../stores/homeBannersAddModal";

const typeOptions = ["local", "international", "mixed"].map((value) => ({ value, label: value }));

export default function AuthBannerSettingsModal({ Interface }) {
	const form = useRecoilValue(authBannerSettingsModalStore);
	const setForm = useSetRecoilState(authBannerSettingsModalStore);
	const patch = (values) => setForm({ ...form, ...values });
	const close = () => setForm(createAuthBannerSettingsForm());

	useEffect(() => {
		Interface.request("get", { class: "data", module: "homeBanners", method: "getAuthBannerSettings" }, {})
			.then((settings) => setForm({ ...createAuthBannerSettingsForm(), ...settings, show: true, loaded: true }))
			.catch((error) => {
				Interface.addNoty("Error", error?.text || error.toString());
				close();
			});
	}, []);

	const save = () => {
		patch({ saving: true });
		Interface.request("set", { class: "data", module: "homeBanners", method: "editAuthBannerSettings" }, form)
			.then(() => {
				Interface.addNoty("Success", "Auth banner settings were updated");
				close();
			})
			.catch((error) => {
				Interface.addNoty("Error", error?.text || error.toString());
				patch({ saving: false });
			});
	};

	const selectType = (field) => <Select selectedOption={typeOptions.find((option) => option.value === form[field])} options={typeOptions} onChange={({ detail }) => patch({ [field]: detail.selectedOption.value })} />;

	return (
		<Modal
			visible={form.show}
			size="medium"
			header="Auth Banner Settings"
			onDismiss={close}
			footer={
				<Box float="right">
					<SpaceBetween direction="horizontal" size="s">
						<Button onClick={close}>Close</Button>
						<Button variant="primary" loading={form.saving} disabled={!form.loaded} onClick={save}>
							Save
						</Button>
					</SpaceBetween>
				</Box>
			}>
			{form.loaded ? (
				<SpaceBetween direction="vertical" size="m">
					<FormField label="Image">
						<input
							type="file"
							accept="image/png"
							onChange={(event) => {
								const file = event.target.files[0];
								if (!file) return;
								if (file.size > MAX_BANNER_BYTES) {
									Interface.addNoty("Error", "Image size must not exceed 1 MB");
									event.target.value = "";
									return;
								}
								const reader = new FileReader();
								reader.onload = ({ target }) => patch({ image: target.result });
								reader.readAsDataURL(file);
							}}
						/>
						{form.image && (
							<Box margin={{ top: "s" }}>
								<img src={form.image} alt="Auth banner" style={{ maxWidth: "100%", maxHeight: "240px" }} />
							</Box>
						)}
					</FormField>
					<FormField label="Title type">{selectType("titleType")}</FormField>
					<FormField label="Title">
						<Input value={form.title} onChange={({ detail }) => patch({ title: detail.value })} />
					</FormField>
					<FormField label="Description type">{selectType("descriptionType")}</FormField>
					<FormField label="Description">
						<Input value={form.description} onChange={({ detail }) => patch({ description: detail.value })} />
					</FormField>
				</SpaceBetween>
			) : (
				<Box padding={{ top: "xxxl", bottom: "xxxl" }} textAlign="center">
					<Spinner size="large" />
				</Box>
			)}
		</Modal>
	);
}
